import {
  Component,
  inject,
  linkedSignal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';

import { M3ButtonComponent } from '@shared/button/m3-button.component';
import { BaseSidebarComponent } from '../base-sidebar/base-sidebar.component';
import { SidebarSectionComponent } from '../sidebar-section/sidebar-section.component';
import { FilterChipGroupComponent } from './filter-chip-group/filter-chip-group.component';
import { FilterGroup } from '../../models';
import { sidebarLabels, sidebarAriaLabels } from '../../localization';
import { FilterSidebarService } from '../../services';

@Component({
  selector: 'app-filter-sidebar',
  standalone: true,
  imports: [
    BaseSidebarComponent,
    SidebarSectionComponent,
    FilterChipGroupComponent,
    M3ButtonComponent,
  ],
  template: `
    <app-base-sidebar
      [title]="title()"
      [closeAriaLabel]="closeAriaLabel()"
      [addContentPadding]="true"
      [showFooter]="true"
      (closed)="onClose()"
    >
      <div class="filter-sections">
        @for (group of editableFilterGroups(); track group.id) {
          <app-sidebar-section
            [title]="group.title"
            [description]="group.description"
          >
            <app-filter-chip-group
              [group]="group"
              (selectionChange)="onSelectionChange($event, group.id)"
            />
          </app-sidebar-section>
        }
      </div>

      <div slot="footer" class="footer-actions">
        <app-m3-button
          variant="standard"
          [disabled]="disableReset()"
          [ariaLabel]="resetAriaLabel()"
          (clicked)="onResetClick()"
        >
          {{ resetLabel() }}
        </app-m3-button>

        <app-m3-button
          variant="filled"
          [ariaLabel]="applyAriaLabel()"
          (clicked)="onApply()"
        >
          {{ applyLabel() }}
        </app-m3-button>
      </div>
    </app-base-sidebar>
  `,
  styles: [
    `
      :host {
        display: contents;
      }

      .filter-sections {
        display: flex;
        flex-direction: column;
        gap: 2rem;
      }

      .footer-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        gap: 1rem;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterSidebarComponent {
  private readonly service = inject(FilterSidebarService);

  private readonly config = computed(() => this.service.state().config);

  // Configuration from service with defaults
  protected readonly title = computed(
    () => this.config()?.title ?? sidebarLabels.filterTitle()
  );
  protected readonly closeAriaLabel = computed(
    () => this.config()?.closeAriaLabel ?? sidebarAriaLabels.filterSidebar()
  );
  protected readonly resetLabel = computed(
    () => this.config()?.resetLabel ?? sidebarLabels.filterReset()
  );
  protected readonly applyLabel = computed(
    () => this.config()?.applyLabel ?? sidebarLabels.filterApply()
  );
  protected readonly resetAriaLabel = computed(
    () => this.config()?.resetAriaLabel ?? sidebarAriaLabels.filterReset()
  );
  protected readonly applyAriaLabel = computed(
    () => this.config()?.applyAriaLabel ?? sidebarAriaLabels.filterApply()
  );

  // Editable state that resets to config.filterGroups whenever config changes
  protected readonly editableFilterGroups = linkedSignal<FilterGroup[]>(() => {
    const groups = this.config()?.filterGroups ?? [];
    return groups.map((group) => ({
      ...group,
      options: group.options.map((option) => ({ ...option })),
    }));
  });

  // Disable reset when no options are selected
  protected readonly disableReset = computed(() => {
    const groups = this.editableFilterGroups();
    return !groups.some((g) => g.options.some((o) => o.selected));
  });

  onSelectionChange(selectedIds: string[], groupId: string): void {
    this.editableFilterGroups.update((groups) =>
      groups.map((group) => {
        if (group.id !== groupId) return group;
        return {
          ...group,
          options: group.options.map((option) => ({
            ...option,
            selected: selectedIds.includes(option.id),
          })),
        };
      })
    );
  }

  onResetClick(): void {
    this.editableFilterGroups.update((groups) =>
      groups.map((group) => ({
        ...group,
        options: group.options.map((option) => ({
          ...option,
          selected: false,
        })),
      }))
    );
  }

  onApply(): void {
    this.service.apply(this.editableFilterGroups());
  }

  onClose(): void {
    this.service.close();
  }
}
